import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useStore, useTopic } from '../lib/store.js';
import { api } from '../lib/api.js';
import type { ServerDoc } from '@panel/shared';
import { IconArrowDown, IconArrowUp, IconChevronDown, IconRefresh } from './Icons.js';

interface Props {
  server: ServerDoc;
  /** False when the agent for this server's machine is currently unreachable.
   *  When false, the RCON input + map-staging buttons are disabled and a
   *  banner is shown — the commands would otherwise hit the hub, queue a
   *  job, and time out because the agent isn't there to receive it. */
  nodeOnline?: boolean;
}

const MAP_URL_RX = /https?:\/\/[^\s]+\.map(\?[^\s]*)?/g;

interface Preset {
  category: string;
  label: string;
  command: string;
  /** When true, the command has a `<value>` placeholder we land the cursor on. */
  fillIn?: boolean;
}

const PRESETS: Preset[] = [
  { category: 'Info',       label: 'Player list',        command: 'playerlist' },
  { category: 'Info',       label: 'Server info',        command: 'serverinfo' },
  { category: 'Info',       label: 'Status',             command: 'status' },
  { category: 'Info',       label: 'Banlist',            command: 'banlist' },

  { category: 'Moderation', label: 'Kick player',        command: 'kick "<steamid_or_name>"',          fillIn: true },
  { category: 'Moderation', label: 'Ban player',         command: 'ban "<steamid>" "reason"',          fillIn: true },
  { category: 'Moderation', label: 'Unban player',       command: 'unban "<steamid>"',                 fillIn: true },
  { category: 'Moderation', label: 'Mute player',        command: 'mute "<steamid>" 60',               fillIn: true },
  { category: 'Moderation', label: 'Teleport player',    command: 'teleport2me "<steamid>"',           fillIn: true },

  { category: 'World',      label: 'Save now',           command: 'server.save' },
  { category: 'World',      label: 'Set time (12)',      command: 'env.time 12' },
  { category: 'World',      label: 'Set time (24h cycle)', command: 'env.daylength 60' },
  { category: 'World',      label: 'Heli call',          command: 'heli.call' },
  { category: 'World',      label: 'Bradley call',       command: 'spawn bradleyapc' },

  { category: 'Server',     label: 'Server message',     command: 'say "<message>"',                   fillIn: true },
  { category: 'Server',     label: 'Restart 5 min',      command: 'restart 300' },
  { category: 'Server',     label: 'Cancel restart',     command: 'restart -1' },
  { category: 'Server',     label: 'Players online',     command: 'global.players' },
  { category: 'Server',     label: 'FPS',                command: 'fps' },
];

export default function Console({ server, nodeOnline = true }: Props) {
  useTopic(`console:${server.Server_ID}`);

  const lines = useStore((s) => s.consoleByServer.get(server.Server_ID) ?? []);
  const [input, setInput] = useState('');
  const [staged, setStaged] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const presetsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!presetsOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!presetsRef.current?.contains(e.target as Node)) setPresetsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [presetsOpen]);

  const applyPreset = (p: Preset) => {
    setInput(p.command);
    setPresetsOpen(false);
    // Drop the cursor onto the first placeholder so the user can type the
    // value without arrowing over there manually.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      if (p.fillIn) {
        const start = p.command.indexOf('<');
        const end = p.command.indexOf('>') + 1;
        if (start >= 0 && end > start) el.setSelectionRange(start, end);
      } else {
        el.setSelectionRange(p.command.length, p.command.length);
      }
    });
  };

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || didInitialScrollRef.current || lines.length === 0) return;
    el.scrollTop = el.scrollHeight;
    didInitialScrollRef.current = true;
  }, [lines.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !didInitialScrollRef.current) return;
    const nearBottom = el.scrollHeight - el.clientHeight - el.scrollTop < 80;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [lines.length]);

  const onSubmit = async () => {
    const text = input.trim();
    if (!text) return;

    const matches = text.match(MAP_URL_RX);
    if (matches && matches.length > 0) {
      setStaged(matches);
      setInput('');
      return;
    }

    setBusy(true);
    try {
      await api.post(`/api/servers/${server.Server_ID}/actions/rcon`, { command: text });
      setInput('');
    } catch (err) {
      alert(`Request failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const stageMaps = async (mode: 'front' | 'back' | 'replace') => {
    setBusy(true);
    try {
      await api.post(`/api/servers/${server.Server_ID}/maps`, { maps: staged, mode });
      setStaged([]);
    } catch (err) {
      alert(`Failed to update map queue: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card flex flex-col h-[480px] overflow-hidden">
      <header className="px-4 h-11 border-b border-bg-border flex items-center justify-between bg-bg-raised/40">
        <div className="flex items-center gap-2">
          <span className="dot bg-status-online" />
          <h3 className="font-semibold text-fg text-sm">Console</h3>
        </div>
        <span className="text-[11px] text-fg-dim tabular-nums">{lines.length} lines</span>
      </header>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 font-mono text-[11px] leading-relaxed text-fg-muted bg-bg">
        {lines.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-fg-dim gap-2">
            <span className="dot-lg bg-bg-border" />
            <span className="text-xs italic">No console output yet…</span>
          </div>
        ) : (
          lines.map((l, i) => (
            <div key={i} className={`whitespace-pre-wrap ${LEVEL_COLORS[l.level] ?? ''}`}>
              <span className="text-fg-dim">[{new Date(l.ts).toLocaleTimeString()}]</span>{' '}
              <span className="text-fg-dim">{l.source}</span>{' '}
              {l.text}
            </div>
          ))
        )}
      </div>

      {staged.length > 0 && (
        <div className="border-t border-bg-border bg-bg-raised px-4 py-3 animate-slide-up">
          <div className="text-xs text-fg-muted mb-2">
            Detected <span className="text-fg font-medium">{staged.length}</span> map URL{staged.length === 1 ? '' : 's'} — add to queue?
          </div>
          <pre className="text-[10px] font-mono text-fg-dim mb-2 max-h-24 overflow-y-auto">{staged.join('\n')}</pre>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => stageMaps('front')} disabled={busy || !nodeOnline} className="btn-primary btn-sm">
              <IconArrowUp className="w-3.5 h-3.5" /> Front of queue
            </button>
            <button onClick={() => stageMaps('back')} disabled={busy || !nodeOnline} className="btn-ghost btn-sm">
              <IconArrowDown className="w-3.5 h-3.5" /> Back of queue
            </button>
            <button onClick={() => stageMaps('replace')} disabled={busy || !nodeOnline} className="btn-ghost btn-sm">
              <IconRefresh className="w-3.5 h-3.5" /> Replace queue
            </button>
            <button onClick={() => setStaged([])} className="btn-quiet btn-sm ml-auto">Cancel</button>
          </div>
        </div>
      )}

      {!nodeOnline && (
        <div className="border-t border-bg-border bg-status-error/5 px-3 py-2 text-xs text-status-error font-medium">
          Agent disconnected — RCON commands disabled until the machine reconnects.
        </div>
      )}

      <div className="border-t border-bg-border px-3 py-2 flex items-center gap-2 bg-bg-raised relative">
        <div ref={presetsRef} className="relative">
          <button
            type="button"
            onClick={() => setPresetsOpen((v) => !v)}
            className="btn-quiet btn-sm !h-7 !px-2"
            aria-haspopup="listbox"
            aria-expanded={presetsOpen}
            title="Insert a preset RCON command"
            disabled={!nodeOnline}
          >
            Presets
            <IconChevronDown className="w-3 h-3" />
          </button>
          {presetsOpen && <PresetMenu onPick={applyPreset} />}
        </div>
        <span className="text-fg-dim font-mono text-xs select-none">{'›'}</span>
        <input
          ref={inputRef}
          className="flex-1 bg-transparent outline-none text-sm font-mono placeholder:text-fg-dim disabled:cursor-not-allowed"
          placeholder={nodeOnline ? 'RCON command, or paste rustmaps URLs…' : 'Disabled while machine is unreachable'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
          disabled={busy || !nodeOnline}
          aria-label="RCON command"
        />
        <button onClick={onSubmit} disabled={busy || !input.trim() || !nodeOnline} className="btn-primary btn-sm">Send</button>
      </div>
    </div>
  );
}

const LEVEL_COLORS: Record<string, string> = {
  warn: 'text-status-warn',
  error: 'text-status-error',
  debug: 'text-fg-dim',
};

function PresetMenu({ onPick }: { onPick: (p: Preset) => void }) {
  const [query, setQuery] = useState('');
  const filtered = query.trim()
    ? PRESETS.filter((p) => `${p.label} ${p.command}`.toLowerCase().includes(query.toLowerCase()))
    : PRESETS;
  const grouped = new Map<string, Preset[]>();
  for (const p of filtered) {
    const list = grouped.get(p.category) ?? [];
    list.push(p);
    grouped.set(p.category, list);
  }
  return (
    <div
      className="absolute bottom-full mb-2 left-0 w-80 max-h-[60vh] overflow-y-auto card shadow-modal animate-slide-up z-30"
      role="listbox"
    >
      <div className="px-3 py-2 border-b border-bg-border bg-bg-raised/40">
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter presets…"
          className="w-full bg-transparent outline-none text-sm placeholder:text-fg-dim"
        />
      </div>
      {filtered.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-fg-dim">No matches</div>
      ) : (
        <div className="py-1">
          {Array.from(grouped.entries()).map(([cat, list]) => (
            <div key={cat} className="py-1">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-semibold text-fg-dim">
                {cat}
              </div>
              {list.map((p) => (
                <button
                  key={p.command}
                  type="button"
                  onClick={() => onPick(p)}
                  className="w-full text-left px-3 py-1.5 hover:bg-bg-hover/60 cursor-pointer transition-colors"
                  role="option"
                >
                  <div className="text-sm text-fg leading-tight">{p.label}</div>
                  <div className="text-[11px] text-fg-dim font-mono mt-0.5 truncate">{p.command}</div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
